import debug from "debug";

import type { Config } from "@jti/fe-common";
import { getConfig, isServerSide } from "@jti/fe-common";

import { injectGoogleReCaptchaScript } from "./injectGoogleReCaptchaScript";

const log = debug("desjti:clients:runGoogleReCaptcha");

declare global {
  interface Window {
    __recaptchaConfiguration?: {
      recaptchaId: string;
    };
  }
}

// Prefer the current config because the window value can be stale after client-side locale navigation.
export const getRecaptchaSiteKey = (config: Config, token = ""): string =>
  token || config["X-RECAPTCHA-ID"] || window.__recaptchaConfiguration?.recaptchaId || "";

export const runGoogleReCaptcha = (token: string, resolve: (text: string) => void, onReject?: () => void) => {
  // On SSR reCAPTCHA is skipped
  if (isServerSide()) {
    resolve("");
    return;
  }

  const config = getConfig();

  // X-RECAPTCHA-FEATURE-TOGGLE and reCaptchaEnabled are the same Builder field under two names
  if (config["X-RECAPTCHA-FEATURE-TOGGLE"] !== "true" && !config.reCaptchaEnabled) {
    resolve("");
    return;
  }

  const recaptchaId = getRecaptchaSiteKey(config, token);

  if (!recaptchaId) {
    resolve("");
    return;
  }

  function executeRecaptcha() {
    return window.grecaptcha?.ready(function () {
      window.grecaptcha.execute(recaptchaId, { action: `submit` }).then(resolve, onReject);
    });
  }

  // Check if reCAPTCHA is already loaded
  if (window.grecaptcha) {
    return executeRecaptcha();
  }

  // Inject the reCAPTCHA script and wait for it to load
  injectGoogleReCaptchaScript(recaptchaId)
    .then(executeRecaptcha)
    .catch(() => {
      // If injecting the reCaptcha script fails
      // We want to try send the request anyway (without token)
      log("Failed to load reCAPTCHA script");
      resolve("");
    });
};

export const getGoogleReCaptchaToken = (token: string) => {
  // On SSR reCAPTCHA is skipped
  if (isServerSide()) {
    return Promise.resolve("");
  }

  return new Promise<string>((resolve, reject) => {
    runGoogleReCaptcha(token, resolve, reject);
  });
};
